/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    type ReactElement,
    type ReactNode,
    type PropsWithChildren,
    useRef,
    useEffect,
    useCallback,
    useState,
} from 'react';
import { useLocation } from 'react-router';
import { Search as SearchIcon, X } from 'lucide-react';
import { Link } from '@/components/link';
import Search from './search';
import CartBadge from './cart-badge';
import UserActions from './user-actions/user-actions';
import WishlistIcon from './wishlist-icon';
import { useTranslation } from 'react-i18next';
import logo from '/images/logo.png';
import { Button } from '@/components/ui/button';
import { SparklesIcon } from '@/components/icons';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { launchChat } from '@/components/shopper-agent';
import { validateShopperAgentConfig } from '@/components/shopper-agent/shopper-agent.utils';
import { UITarget } from '@/targets/ui-target';
import { Region } from '@/components/region';
import { cn } from '@/lib/utils';
import { DEFAULT_SITE_NAME, DEFAULT_SITE_NAME_KEY } from '@/components/seo-meta';
import type { PageWithComponentData } from '@/lib/page-designer/page-loader.server';

// Intrinsic size of logo.png — lets the browser reserve the box before the image loads (no CLS).
const LOGO_WIDTH = 657;
const LOGO_HEIGHT = 225;
// Fixed 200x69 to match the production storefront's logo size (logo.png's 657x225 aspect ratio).
// `shrink-0 max-w-none` keeps the grid from squeezing it. Below `sm` (640px) the main header swaps it
// for the child icon (`.sj-child-logo`, brand.css); the checkout header keeps it.
const LOGO_SIZE_CLASSES = 'w-[200px] h-[69px] max-w-none shrink-0';

interface HeaderProps extends PropsWithChildren {
    beforeHeader?: ReactNode;
    variant?: 'full' | 'checkout';
    headerBannerPage?: Promise<PageWithComponentData | null> | PageWithComponentData | null;
}

const DONATE_HREF = 'https://giftshop.stjude.org/gift-of-donation-pd.html';

// Isolates the `useLocation()` subscription so that route changes only re-render the Search component (via key reset)
// without cascading a re-render through the entire `Header` tree, which would unnecessarily re-render the navigation
// menu and other stable children.
function LocationKeyedSearch() {
    const location = useLocation();
    return <Search key={`${location.pathname}${location.search}`} />;
}

export default function Header({ children, beforeHeader, variant = 'full', headerBannerPage }: HeaderProps): ReactElement {
    const { t } = useTranslation('header');
    // Brand name (not "Home") as the logo's alt text — it's the link's accessible name and what
    // search engines index for the logo image.
    const logoAlt = t(`common:${DEFAULT_SITE_NAME_KEY}`, { defaultValue: DEFAULT_SITE_NAME });
    const headerRef = useRef<HTMLElement>(null);
    const searchContainerRef = useRef<HTMLDivElement>(null);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    // The collapse-to-icon behavior only exists at the `lg` breakpoint (mobile always shows the
    // full search row), so `inert` below must only kick in there — otherwise the mobile search
    // input would be inert by default since `isSearchOpen` never flips true on mobile.
    const [isDesktopViewport, setIsDesktopViewport] = useState(false);
    const config = useConfig();
    const showChat =
        variant === 'full' &&
        (config.commerceAgent?.enabled === 'true' || config.commerceAgent?.enabled === true) &&
        validateShopperAgentConfig(config.commerceAgent);
    const updateHeaderHeight = useCallback(() => {
        if (headerRef.current) {
            const height = `${headerRef.current.offsetHeight}px`;
            headerRef.current.style.setProperty('--header-height', height);
            document.documentElement.style.setProperty('--header-height', height);
        }
    }, []);

    useEffect(() => {
        const el = headerRef.current;
        if (!el) return;
        updateHeaderHeight();
        const observer = new ResizeObserver(updateHeaderHeight);
        observer.observe(el);
        return () => observer.disconnect();
    }, [updateHeaderHeight]);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(min-width: 1024px)');
        const updateMatch = () => setIsDesktopViewport(mediaQuery.matches);
        updateMatch();
        mediaQuery.addEventListener('change', updateMatch);
        return () => mediaQuery.removeEventListener('change', updateMatch);
    }, []);

    // Closes the expanded desktop search on an outside click or Escape. Scoped to when it's
    // open so idle headers don't pay for a document-level listener.
    useEffect(() => {
        if (!isSearchOpen) return;
        const handlePointerDown = (event: MouseEvent) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
                setIsSearchOpen(false);
            }
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsSearchOpen(false);
            }
        };
        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isSearchOpen]);

    useEffect(() => {
        if (isSearchOpen) {
            searchContainerRef.current?.querySelector('input')?.focus();
        }
    }, [isSearchOpen]);

    if (variant === 'checkout') {
        return (
            <header ref={headerRef} className="bg-header-background text-header-foreground sticky top-0 z-50">
                <div className="section-container">
                    <div className="flex items-center h-16">
                        <Link to="/" className="flex-shrink-0 flex items-center" data-testid="header-logo">
                            <img
                                src={logo}
                                alt={logoAlt}
                                width={LOGO_WIDTH}
                                height={LOGO_HEIGHT}
                                className={`${LOGO_SIZE_CLASSES} [filter:var(--header-logo-filter)]`}
                            />
                        </Link>
                        <div className="flex-1" />
                        <CartBadge />
                    </div>
                </div>
            </header>
        );
    }

    return (
        <header ref={headerRef} className="bg-header-background text-header-foreground sticky top-0 z-50">
            {headerBannerPage ? (
                <Region page={headerBannerPage} regionId="topheaderbanner" />
            ) : null}
            <div className="flex justify-end section-container">{beforeHeader}</div>
            {/* Main nav row - solid crimson block on mobile (menu, logo, cart all in white), plain white row on desktop */}
            <div className="bg-header-accent-background lg:bg-transparent">
                <div className="section-container py-6">
                    {/*
                     * One grid drives both breakpoints. Every child (logo, search, nav, donate CTA,
                     * utility icons) mounts once and is repositioned via grid line numbers instead of
                     * being duplicated behind `hidden`/`lg:flex` pairs, or nested inside an extra wrapper
                     * — nesting would break both the grid placement (col/row-start only works on direct
                     * grid children) and the mobile full-width search row (a nested child can never be
                     * wider than its own parent cell).
                     * Mobile (3 cols x 2 rows): row 1 = nav | logo | end-icons, row 2 = search (full width).
                     * Desktop (5 cols x 1 row): logo | nav | donate | search | end-icons — the original
                     * 4-block horizontal layout (Logo, Nav, Donate, Utility icons/Search/Account/Cart).
                     * Source order is Logo, Search, Utility icons (Search → Account → Cart read
                     * contiguously), then Nav, then Donate — grid line numbers place Nav/Donate visually
                     * between Logo and Search without disturbing that natural reading/tab order.
                     */}
                    <div className="grid items-center gap-x-2 gap-y-3 grid-cols-[auto_0fr_auto] lg:grid-cols-[auto_1fr_auto_auto_auto] lg:gap-x-6">
                        {/* Logo - centered white silhouette on mobile (crimson row behind it), plain start-aligned on desktop */}
                        <Link
                            to="/"
                            data-testid="header-logo"
                            // Below `sm` the <img> is display:none (child icon is a CSS background), so its
                            // alt no longer names the link — label the link itself.
                            aria-label={logoAlt}
                            className="col-start-2 row-start-1 justify-self-center px-4 py-2 lg:col-start-1 lg:justify-self-start lg:px-0 lg:py-0 sj-child-logo">
                            <img
                                src={logo}
                                alt={logoAlt}
                                width={LOGO_WIDTH}
                                height={LOGO_HEIGHT}
                                className={`${LOGO_SIZE_CLASSES} [filter:brightness(0)_invert(1)] lg:[filter:var(--header-logo-filter)]`}
                            />
                        </Link>

                        {/* Search - white bar full-width row under the crimson mobile top row; on desktop it
                            collapses to an icon + "Search" caption in the utility group (matching the
                            Account/Cart captions) and expands into the input inline on click or focus.
                            Kept before Navigation in DOM order (screen reader / tab order) even though
                            the mobile grid visually places the nav trigger above it. */}
                        <div
                            ref={searchContainerRef}
                            data-testid="header-search-row"
                            className="col-start-1 col-span-3 row-start-2 w-full rounded-md bg-header-background px-3 py-2 lg:col-start-4 lg:col-span-1 lg:row-start-1 lg:flex lg:items-center lg:justify-end lg:w-auto lg:rounded-none lg:bg-transparent lg:px-0 lg:py-0">
                            <Button
                                type="button"
                                variant="ghost"
                                data-testid="header-search-toggle"
                                className="hidden lg:inline-flex lg:items-center lg:h-9 lg:gap-2 lg:px-5 lg:py-2 cursor-pointer hover:bg-transparent hover:opacity-50 transition-opacity"
                                aria-expanded={isSearchOpen}
                                aria-controls="header-search-expand"
                                aria-label={isSearchOpen ? t('closeSearch') : t('openSearch')}
                                onFocus={() => setIsSearchOpen(true)}
                                onClick={() => setIsSearchOpen(!isSearchOpen)}>
                                {isSearchOpen ? <X className="size-5" /> : <SearchIcon className="size-5" />}
                                {!isSearchOpen && (
                                    <span className="text-xs font-medium" aria-hidden="true">
                                        {t('searchLabel')}
                                    </span>
                                )}
                            </Button>
                            <div
                                id="header-search-expand"
                                inert={isDesktopViewport && !isSearchOpen}
                                className={cn(
                                    'lg:overflow-hidden lg:transition-[width,opacity] lg:duration-200 lg:ease-out',
                                    isSearchOpen ? 'lg:w-56 xl:w-64 lg:ml-2 lg:opacity-100' : 'lg:w-0 lg:opacity-0'
                                )}>
                                <LocationKeyedSearch />
                            </div>
                        </div>

                        {/* Utility icons group - natural DOM order continues Search → Account → Cart
                            (UITarget/chat are optional extras ahead of Account, not part of that core
                            trio). Proportional gap-x keeps the icons evenly spaced instead of relying on
                            each button's own padding, so they don't crowd now that Account/Cart carry
                            captions. White on crimson row on mobile, plain end zone on desktop. */}
                        <div
                            data-testid="header-end-actions"
                            className="col-start-3 row-start-1 flex items-center gap-x-1 justify-self-end text-header-accent-foreground lg:col-start-5 lg:row-start-1 lg:gap-x-3 lg:text-header-foreground">
                            <UITarget targetId="sfcc.header.before.cart" />
                            {showChat && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="cursor-pointer lg:px-4 px-1 hover:bg-transparent hover:opacity-50 transition-opacity"
                                    onClick={() => launchChat()}
                                    aria-label={t('openChat')}>
                                    <SparklesIcon />
                                </Button>
                            )}
                            <UserActions />
                            <WishlistIcon />
                            <CartBadge />
                        </div>

                        {/* Navigation Menu - hamburger on mobile (unchanged internally), mega menu on desktop */}
                        <div
                            data-testid="header-nav"
                            className="col-start-1 row-start-1 justify-self-start p-1.5 text-header-accent-foreground lg:col-start-2 lg:row-start-1 lg:p-0 lg:text-header-foreground">
                            {children}
                        </div>

                        {/* Donate Now - third block of the horizontal layout, desktop only */}
                        <a
                            href={DONATE_HREF}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="header-donate-cta"
                            className="hidden lg:col-start-3 lg:row-start-1 lg:inline-flex items-center justify-center px-5 py-2 rounded-full text-sm font-semibold bg-header-accent-background text-header-accent-foreground transition-all btn-cta-donate">
                            Donate Now
                        </a>
                    </div>
                </div>
            </div>

            <div className="section-container pb-auto ">
                <UITarget targetId="sfcc.header.bnpl.banner" />
            </div>
        </header>
    );
}
